import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, FileText, Loader2, Building2, User, IndianRupee, Receipt, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { toLocalISODate } from '../lib/formatUtils';

const SAC_OPTIONS = [
  { code: '998314', label: 'Web Development' },
  { code: '998311', label: 'Digital Marketing' },
  { code: '998315', label: 'Hosting' },
  { code: '997331', label: 'SaaS' },
];

const INDIAN_STATES = [
  "Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam",
  "Bihar", "Chandigarh", "Chhattisgarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh",
  "Jammu and Kashmir", "Jharkhand", "Karnataka", "Kerala", "Ladakh",
  "Lakshadweep", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya",
  "Mizoram", "Nagaland", "Odisha", "Puducherry", "Punjab", "Rajasthan",
  "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh",
  "Uttarakhand", "West Bengal",
];

const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amt || 0);

export const RaiseInvoicePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialProjectId = searchParams.get('project');
  const defaultType = searchParams.get('type') || 'proforma';
  const initialDescription = searchParams.get('description') || '';
  const initialAmount = searchParams.get('amount') || '';

  const [projectId, setProjectId] = useState(initialProjectId || '');
  const [projSearch, setProjSearch] = useState('');
  const [projResults, setProjResults] = useState([]);
  const [projOpen, setProjOpen] = useState(false);
  const [projSearching, setProjSearching] = useState(false);

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [project, setProject] = useState(null);
  const [client, setClient] = useState(null);
  const [company, setCompany] = useState({});
  const [nextNumber, setNextNumber] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState('');

  const [form, setForm] = useState({
    invoice_type: defaultType,
    invoice_date: toLocalISODate(),
    description: initialDescription,
    sac_code: '998314',
    amount: initialAmount,
    include_gst: true,
    client_name: '',
    client_address: '',
    client_state: '',
    client_country: 'India',
    client_gstin: '',
    notes: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [
        api.get('/invoices/company-settings'),
        api.get('/transactions/bank-accounts'),
      ];
      if (projectId) promises.push(api.get(`/invoices/project-details/${projectId}`));
      promises.push(api.get(`/invoices/next-number?type=${defaultType}`));

      const results = await Promise.all(promises);

      // Company settings
      if (results[0].data.success) setCompany(results[0].data.data);

      // Bank accounts
      if (results[1].data.success) {
        const accounts = results[1].data.data;
        setBankAccounts(accounts);
        const primary = accounts.find(a => a.is_primary) || accounts[0];
        if (primary) setSelectedBankId(primary.id);
      }

      // Project + client details
      const projIdx = projectId ? 2 : -1;
      if (projectId && results[projIdx]?.data.success) {
        const { project: proj, client: cli } = results[projIdx].data.data;
        setProject(proj);
        setClient(cli);
        setForm(prev => ({
          ...prev,
          client_name: cli?.company_name || proj?.client_name || '',
          client_address: cli?.address || '',
          client_state: cli?.state || '',
          client_country: cli?.country || 'India',
          client_gstin: cli?.gstin || cli?.gst || '',
        }));
      }

      // Next number
      const numResult = results[results.length - 1];
      if (numResult?.data.success) setNextNumber(numResult.data.data.invoice_number);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [projectId, defaultType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Project autosuggest: debounced filter (client-side since /projects is small)
  useEffect(() => {
    if (projectId) return;
    if (!projSearch || projSearch.length < 1) { setProjResults([]); return; }
    const t = setTimeout(async () => {
      setProjSearching(true);
      try {
        const res = await api.get('/projects?limit=200');
        const all = res.data?.data || [];
        const q = projSearch.toLowerCase();
        const filtered = all.filter(p =>
          (p.name || '').toLowerCase().includes(q) ||
          (p.client_name || '').toLowerCase().includes(q)
        ).slice(0, 15);
        setProjResults(filtered);
      } catch {} finally { setProjSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [projSearch, projectId]);

  const selectProject = (p) => {
    setProjectId(p.id);
    setProjSearch('');
    setProjResults([]);
    setProjOpen(false);
  };
  const clearProject = () => {
    setProjectId('');
    setProject(null);
    setProjSearch('');
  };

  // Refresh invoice number when type changes
  const handleTypeChange = async (type) => {
    setForm(prev => ({ ...prev, invoice_type: type }));
    try {
      const res = await api.get(`/invoices/next-number?type=${type}`);
      if (res.data.success) setNextNumber(res.data.data.invoice_number);
    } catch {}
  };

  const amount = parseFloat(form.amount) || 0;
  const gstAmount = form.include_gst ? Math.round(amount * 0.18 * 100) / 100 : 0;
  const grandTotal = Math.round((amount + gstAmount) * 100) / 100;
  const selectedBank = bankAccounts.find(b => b.id === selectedBankId);

  const handleGenerate = async () => {
    if (!form.client_name?.trim()) { toast.error('Client Name is required'); return; }
    if (!form.client_address?.trim()) { toast.error('Client Address is required'); return; }
    if (!form.client_state?.trim()) { toast.error('Client State is required'); return; }
    if (!form.client_country?.trim()) { toast.error('Client Country is required'); return; }
    if (!form.description) { toast.error('Description is required'); return; }
    if (amount <= 0) { toast.error('Amount must be greater than 0'); return; }

    setGenerating(true);
    try {
      const payload = {
        ...form,
        project_id: projectId,
        amount: amount,
        bank_account_id: selectedBankId,
      };
      const res = await api.post('/invoices/generate', payload);
      if (res.data.success) {
        toast.success(res.data.message);
        // Render the PDF server-side so the freshness pill is correct
        // and the email flow can attach it without an extra round-trip.
        try {
          await api.post(`/invoices/${res.data.data.id}/regenerate-pdf`);
        } catch (pdfErr) {
          console.warn('PDF generation failed:', pdfErr);
        }
        navigate('/invoices');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl mx-auto" data-testid="raise-invoice-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
          <div>
            <h1 className="text-xl font-bold">Raise Invoice</h1>
            <p className="text-sm text-muted-foreground">{project?.name || 'New Invoice'}</p>
          </div>
        </div>
        <Badge className={form.invoice_type === 'proforma' ? 'bg-amber-500/10 text-amber-500' : 'bg-blue-500/10 text-blue-500'}>
          {form.invoice_type === 'proforma' ? 'Proforma Invoice' : 'Tax Invoice'}
        </Badge>
      </div>

      {/* Project Autosuggest — show when no project locked yet */}
      {!projectId ? (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardHeader className="pb-3"><CardTitle className="text-base">Select Project</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Type to search projects..."
                value={projSearch}
                onChange={(e) => { setProjSearch(e.target.value); setProjOpen(true); }}
                onFocus={() => setProjOpen(true)}
                className="pl-9"
                data-testid="project-autosuggest-input"
              />
              {projOpen && (projSearch || projResults.length > 0) && (
                <div className="absolute z-30 left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-md border bg-background shadow-lg">
                  {projSearching ? (
                    <div className="p-3 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Searching...</div>
                  ) : projResults.length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground">No matching projects.</div>
                  ) : projResults.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => selectProject(p)}
                      className="w-full text-left px-3 py-2 hover:bg-muted/50 border-b border-border/30 last:border-0"
                      data-testid={`project-suggest-${p.id}`}
                    >
                      <div className="font-medium text-sm">{p.name}</div>
                      <div className="text-xs text-muted-foreground">{p.client_name || '—'}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Pick the project this invoice is for. Client details will be auto-filled.</p>
          </CardContent>
        </Card>
      ) : project && (
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-emerald-500/15 flex items-center justify-center"><Building2 className="h-4 w-4 text-emerald-500" /></div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Linked Project</p>
              <p className="text-sm font-semibold">{project.name} <span className="font-normal text-muted-foreground">— {project.client_name || ''}</span></p>
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={clearProject} data-testid="clear-project-btn"><X className="h-4 w-4 mr-1" />Change</Button>
          </CardContent>
        </Card>
      )}

      {/* Invoice Preview Card */}
      <Card className="border-2">
        <CardContent className="p-6 space-y-6">
          {/* Type & Number Row */}
          <div className="flex flex-col sm:flex-row justify-between gap-4 pb-4 border-b border-border">
            <div className="space-y-2">
              <Label>Invoice Type</Label>
              <Select value={form.invoice_type} onValueChange={handleTypeChange}>
                <SelectTrigger className="w-[200px]" data-testid="invoice-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="proforma">Proforma Invoice</SelectItem>
                  <SelectItem value="tax">Tax Invoice</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-right space-y-1">
              <p className="text-xs text-muted-foreground uppercase">Invoice Number</p>
              <p className="text-xl font-mono font-bold text-primary" data-testid="invoice-number">{nextNumber}</p>
              <div className="flex items-center gap-2 justify-end">
                <Label className="text-xs">Date</Label>
                <Input type="date" className="w-[160px] h-8 text-sm" value={form.invoice_date} onChange={e => setForm(f => ({...f, invoice_date: e.target.value}))} data-testid="invoice-date" />
              </div>
            </div>
          </div>

          {/* From / To Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* FROM - Company */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground uppercase">
                <Building2 className="h-4 w-4" />From
              </div>
              <div className="p-4 rounded-lg bg-muted/20 border border-border space-y-1">
                <p className="font-bold text-base">{company.company_name || 'Company Name'}</p>
                <p className="text-sm text-muted-foreground">{company.company_address || 'Address not set'}</p>
                <p className="text-sm text-muted-foreground">{company.company_state || ''}{company.company_state ? ', India' : ''}</p>
                {company.company_gst && <p className="text-xs font-mono">GSTIN: {company.company_gst}</p>}
                {company.company_pan && <p className="text-xs font-mono">PAN: {company.company_pan}</p>}
                <p className="text-xs text-muted-foreground">{company.company_email} | {company.company_phone}</p>
              </div>
            </div>

            {/* TO - Client */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground uppercase">
                <User className="h-4 w-4" />To
              </div>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs">Client Name *</Label>
                  <Input value={form.client_name} onChange={e => setForm(f => ({...f, client_name: e.target.value}))} data-testid="inv-client-name" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Address *</Label>
                  <Textarea value={form.client_address} onChange={e => setForm(f => ({...f, client_address: e.target.value}))} rows={2} className="resize-none text-sm" data-testid="inv-client-address" required />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">State *</Label>
                    <Select value={form.client_state || '_none'} onValueChange={v => setForm(f => ({...f, client_state: v === '_none' ? '' : v}))}>
                      <SelectTrigger className="h-8 text-sm" data-testid="inv-client-state"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">Select state</SelectItem>
                        {INDIAN_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Country *</Label>
                    <Input value={form.client_country} onChange={e => setForm(f => ({...f, client_country: e.target.value}))} className="h-8 text-sm" required />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">GSTIN</Label>
                  <Input value={form.client_gstin} onChange={e => setForm(f => ({...f, client_gstin: e.target.value}))} placeholder="e.g. 27AAGCA5456M2Z8" className="h-8 text-sm font-mono" data-testid="inv-client-gstin" />
                </div>
              </div>
            </div>
          </div>

          {/* Line Item */}
          <div className="space-y-3 pt-4 border-t border-border">
            <p className="text-sm font-semibold text-muted-foreground uppercase flex items-center gap-2"><FileText className="h-4 w-4" />Line Item</p>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">Description *</Label>
                <Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={2} className="resize-none text-sm" placeholder="e.g. Sales Alerting MVP System Advance (40%)" data-testid="inv-description" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">SAC Code</Label>
                  <Select value={form.sac_code} onValueChange={v => setForm(f => ({...f, sac_code: v}))}>
                    <SelectTrigger data-testid="inv-sac-code"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SAC_OPTIONS.map(o => <SelectItem key={o.code} value={o.code}>{o.label} ({o.code})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Amount (INR) *</Label>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground font-mono">&#8377;</span>
                    <Input type="number" value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))} className="text-sm font-mono" placeholder="0.00" data-testid="inv-amount" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* GST & Totals */}
          <div className="pt-4 border-t border-border">
            <div className="flex items-center gap-2 mb-4">
              <Checkbox id="include-gst" checked={form.include_gst} onCheckedChange={v => setForm(f => ({...f, include_gst: v}))} data-testid="inv-include-gst" />
              <label htmlFor="include-gst" className="text-sm font-medium cursor-pointer">Include GST (18%)</label>
            </div>
            <div className="flex justify-end">
              <div className="w-full max-w-xs space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-mono" data-testid="inv-subtotal">{formatCurrency(amount)}</span>
                </div>
                {form.include_gst && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">GST (18%)</span>
                    <span className="font-mono" data-testid="inv-gst-amount">{formatCurrency(gstAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-base font-bold pt-2 border-t border-border">
                  <span>Grand Total</span>
                  <span className="text-primary font-mono" data-testid="inv-grand-total">{formatCurrency(grandTotal)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Proforma disclaimer */}
          {form.invoice_type === 'proforma' && (
            <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 text-center">
              <p className="text-xs text-amber-500 font-medium italic">This is not a tax invoice</p>
            </div>
          )}

          {/* Bank Details */}
          {bankAccounts.length > 0 && (
            <div className="pt-4 border-t border-border">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase">Bank Account Details</p>
                <Select value={selectedBankId} onValueChange={setSelectedBankId}>
                  <SelectTrigger className="w-[240px] h-8 text-sm" data-testid="inv-bank-select"><SelectValue placeholder="Select bank account" /></SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map(b => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}{b.is_primary ? ' (Primary)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedBank && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div><p className="text-muted-foreground">Account Name</p><p className="font-medium">{selectedBank.name}</p></div>
                  <div><p className="text-muted-foreground">Bank</p><p className="font-medium">{selectedBank.bank}</p></div>
                  <div><p className="text-muted-foreground">Account No.</p><p className="font-medium font-mono">{selectedBank.account_number}</p></div>
                  <div><p className="text-muted-foreground">IFSC</p><p className="font-medium font-mono">{selectedBank.ifsc}</p></div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generate Button */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
        <Button onClick={handleGenerate} disabled={generating} className="px-8" data-testid="generate-invoice-btn">
          {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Receipt className="h-4 w-4 mr-2" />}
          Generate Invoice
        </Button>
      </div>
    </div>
  );
};
